/**
 * OrbitControls bound to a pane's own View + Camera instead of the scene's.
 *
 * The base OrbitControls reads `this.scene.view` and `this.scene.getActiveCamera()`
 * to operate. We don't modify OrbitControls itself; instead, when `setScene()`
 * is called, we hand it a Proxy over the real scene that intercepts `view` and
 * `getActiveCamera` and returns this pane's instances. All other properties
 * (pointclouds, scenePointCloud, etc.) pass through to the real scene unchanged.
 *
 * `lockRotation: true` zeroes the yaw/pitch accumulators each frame, so left-drag
 * rotation is suppressed while pan (middle-drag) and zoom (wheel) keep working.
 */

import { OrbitControls } from '../../navigation/OrbitControls.js';

export class PaneOrbitControls extends OrbitControls {
  /**
   * @param {object} viewer
   * @param {object} opts
   * @param {import('../View.js').View} opts.view
   * @param {THREE.Camera} opts.camera
   * @param {boolean} [opts.lockRotation]
   */
  constructor(viewer, opts) {
    super(viewer);
    this.paneView = opts.view;
    this.paneCamera = opts.camera;
    this.lockRotation = !!opts.lockRotation;
  }

  setScene(realScene) {
    const paneView = this.paneView;
    const paneCamera = this.paneCamera;
    // Proxy: most properties pass through, but `view` and `getActiveCamera`
    // are intercepted so OrbitControls operates on this pane's data.
    this.scene = new Proxy(realScene, {
      get(target, prop, receiver) {
        if (prop === 'view') return paneView;
        if (prop === 'getActiveCamera') return () => paneCamera;
        return Reflect.get(target, prop, receiver);
      },
      set(target, prop, value, receiver) {
        if (prop === 'view') {
          // Should not happen; ignore to avoid accidental scene mutation.
          return true;
        }
        return Reflect.set(target, prop, value, receiver);
      },
    });
  }

  update(delta) {
    if (this.lockRotation) {
      // In rotation-locked panes (top / N-S / E-W ortho), repurpose the
      // accumulated yaw / pitch deltas — produced by left-button drag — as
      // pan deltas. Without this, left-drag would be silently dropped and
      // the user could only pan via middle-drag, which is not discoverable.
      //
      // The parent's drag handler scales LEFT deltas by `rotationSpeed` and
      // MIDDLE deltas by 1; we undo that scaling so the resulting pan speed
      // matches what a middle-drag of the same screen distance would
      // produce. Sign convention is identical to MIDDLE drag pass-through.
      if (this.yawDelta !== 0 || this.pitchDelta !== 0) {
        const rs = this.rotationSpeed || 1;
        this.panDelta.x += this.yawDelta / rs;
        this.panDelta.y += this.pitchDelta / rs;
        this.yawDelta = 0;
        this.pitchDelta = 0;
      }
    }
    super.update(delta);
  }
}
